import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { confirmDialog } from '../components/ConfirmDialog';

const containerStyle = {
  padding: '1rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const gridStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  marginTop: '1rem',
  backgroundColor: '#f8f9fa',
  padding: '0.5rem',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
};

const baseTileStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: '1rem',
  border: '2px solid #e9ecef',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  width: '100%',
  marginBottom: '0.5rem'
};

const tileStyleHover = {
  transform: 'translateY(-2px)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  borderColor: '#3498db',
  backgroundColor: '#ffffff'
};

const tileHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '0.5rem',
  flexWrap: 'wrap',
  gap: '0.25rem'
};

const tileTitleStyle = {
  fontSize: '1.125rem',
  fontWeight: '600',
  color: '#2c3e50',
  margin: 0,
  flex: '1',
  minWidth: '200px'
};

const tileCategoryStyle = {
  background: '#46923c',
  color: 'white',
  padding: '0.125rem 0.5rem',
  borderRadius: '20px',
  fontSize: '0.875rem',
  fontWeight: '500',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
};

const tileStatsStyle = {
  display: 'flex',
  gap: '1rem',
  marginTop: '0.5rem',
  flexWrap: 'wrap'
};

const statItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0.25rem',
  background: 'rgba(108, 117, 125, 0.08)',
  borderRadius: '6px',
  borderLeft: '3px solid #6c757d',
  flex: '1',
  minWidth: '120px'
};

const statLabelStyle = {
  fontSize: '0.875rem',
  color: '#6c757d',
  margin: 0,
  fontWeight: '500'
};

const statValueStyle = {
  fontSize: '1.125rem',
  fontWeight: '700',
  color: '#2c3e50',
  margin: '0.25rem 0 0 0'
};

const attemptsStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '0.5rem',
  padding: '0.5rem',
  background: 'rgba(108, 117, 125, 0.08)',
  borderRadius: '6px',
  borderLeft: '3px solid #6c757d'
};

const attemptsLabelStyle = {
  fontSize: '0.875rem',
  color: '#6c757d',
  margin: 0,
  fontWeight: '600'
};

const attemptsValueStyle = {
  fontSize: '1rem',
  fontWeight: '700',
  color: '#495057',
  margin: 0
};

const loadingStyle = {
  textAlign: 'center',
  padding: '2rem'
};

const spinnerStyle = {
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #3498db',
  borderRadius: '50%',
  width: '50px',
  height: '50px',
  animation: 'spin 1s linear infinite',
  margin: '0 auto 0.5rem auto'
};

const skeletonTileStyle = {
  height: '120px',
  background: 'linear-gradient(90deg, #e9ecef 25%, #dee2e6 50%, #e9ecef 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 2s infinite',
  borderRadius: '8px',
  marginBottom: '0.5rem'
};

const paginationStyle = {
  display: 'flex',
  justifyContent: 'center',
  padding: '1rem',
  gap: '0.25rem'
};

const buttonStyle = {
  padding: '0.5rem 1rem',
  borderRadius: '8px',
  border: '1px solid #ddd',
  backgroundColor: '#f8f9fa',
  cursor: 'pointer',
  fontWeight: '500',
  transition: 'all 0.2s ease'
};

const deleteButtonStyle = {
  padding: '0.25rem 0.5rem',
  borderRadius: '6px',
  backgroundColor: '#dc3545',
  color: 'white',
  border: '1px solid #dc3545',
  fontSize: '0.875rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  marginLeft: '0.25rem'
};

const deleteButtonHoverStyle = {
  backgroundColor: '#c82333',
  borderColor: '#bd2130'
};

const adminDeleteButtonStyle = {
  ...deleteButtonStyle,
  backgroundColor: '#6f42c1',
  borderColor: '#6f42c1'
};

const adminDeleteButtonHoverStyle = {
  backgroundColor: '#5a2d91',
  borderColor: '#5a2d91'
};

const activeButtonStyle = {
  ...buttonStyle,
  backgroundColor: '#3498db',
  color: 'white',
  borderColor: '#3498db'
};

const disabledButtonStyle = {
  ...buttonStyle,
  opacity: '0.6',
  cursor: 'not-allowed'
};

// Add keyframes for animations
const style = document.createElement('style');
style.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .tile-hover:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    border-color: #3498db !important;
    background-color: #ffffff !important;
  }
`;
document.head.appendChild(style);

const ResultsOverview = () => {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [dateFilter, setDateFilter] = useState('');
  const [dateSort, setDateSort] = useState('recent');
  const [userRole, setUserRole] = useState('user'); // 'user' or 'admin'
  const resultsPerPage = 10;
  const navigate = useNavigate();

  const fetchOverview = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page,
        limit: resultsPerPage
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (sortConfig.key) {
        params.sortBy = sortConfig.key;
        params.sortOrder = sortConfig.direction;
      }

      if (dateFilter) {
        params.createdAt = dateFilter;
      }

      if (dateSort) {
        params.dateSort = dateSort;
      }

      const response = await api.get('/tests/results/all', { params });
      setOverview(response.data.results);
      setTotalPages(response.data.pagination.totalPages);
      setTotalResults(response.data.pagination.totalResults);
      setCurrentPage(response.data.pagination.currentPage);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch results overview');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, sortConfig]);

  // Check user role on component mount
  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const response = await api.get('/auth/me');
        setUserRole(response.data.user.isAdmin ? 'admin' : 'user');
      } catch (error) {
        console.error('Failed to fetch user role:', error);
        setUserRole('user');
      }
    };
    checkUserRole();
  }, []);

  useEffect(() => {
    fetchOverview(currentPage);
  }, [fetchOverview, currentPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchOverview(1);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
    fetchOverview(1);
  };


  const handleDateSortChange = (e) => {
    const value = e.target.value;
    setDateSort(value);
    setCurrentPage(1);
    fetchOverview(1);
  };

  const handleDeleteTest = async (testId, testName) => {
    try {
      const confirmed = await confirmDialog(
        `Delete "${testName}"?`,
        'This will delete all attempts and results for this test. This action cannot be undone.'
      );
      
      if (!confirmed) return;

      setLoading(true);
      await api.delete(`/tests/${testId}`);
      
      // Show success message
      alert(`Successfully deleted test "${testName}" and all its attempts.`);
      
      // Refresh the overview
      fetchOverview(currentPage);
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('Failed to delete the test. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAttempt = async (testId, submissionId) => {
    try {
      const confirmed = await confirmDialog(
        'Delete this attempt?',
        'This will delete only this specific attempt result. This action cannot be undone.'
      );
      
      if (!confirmed) return;

      setLoading(true);
      await api.delete(`/tests/${testId}/submissions/${submissionId}`);
      
      // Show success message
      alert('Successfully deleted the attempt.');
      
      // Refresh the overview
      fetchOverview(currentPage);
    } catch (error) {
      console.error('Error deleting attempt:', error);
      alert('Failed to delete the attempt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Compute averages and get latest submission date
  const tiles = overview.map((group) => {
    const { test, submissions } = group;
    const avgAcc = submissions.length
      ? Math.round(submissions.reduce((sum, s) => sum + s.accuracy, 0) / submissions.length)
      : 0;
    const avgWpm = submissions.length
      ? Math.round(submissions.reduce((sum, s) => sum + s.wpm, 0) / submissions.length)
      : 0;

    // Get the most recent submission date
    const latestSubmission = submissions.length > 0
      ? new Date(Math.max(...submissions.map(s => new Date(s.createdAt).getTime())))
      : null;

      return {
        id: test.id,
        name: test.name,
        category: test.category,
        attempts: submissions.length,
        avgAcc,
        avgWpm,
        userRank: group.userRank,
        latestSubmission: latestSubmission,
        submissions: submissions // Include submissions for individual attempt deletion
      };
  });

  const handleTileClick = (tileId) => {
    navigate(`/tests/${tileId}/results`);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const delta = 2;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        pageNumbers.push(i);
      } else if (
        i === currentPage - delta - 1 ||
        i === currentPage + delta + 1
      ) {
        pageNumbers.push('...');
      }
    }

    return (
      <div style={paginationStyle}>
        <button
          style={currentPage === 1 || loading ? disabledButtonStyle : buttonStyle}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          Previous
        </button>

        {pageNumbers.map((number, index) => (
          number === '...' ? (
            <span key={`ellipsis-${index}`} style={{ padding: '0 0.5rem', color: '#6c757d' }}>...</span>
          ) : (
            <button
              key={number}
              style={number === currentPage ? activeButtonStyle : buttonStyle}
              onClick={() => handlePageChange(number)}
              disabled={loading}
            >
              {number}
            </button>
          )
        ))}

        <button
          style={currentPage === totalPages || loading ? disabledButtonStyle : buttonStyle}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
        >
          Next
        </button>
      </div>
    );
  };

  // Render skeleton loaders when loading and no data is available
  if (loading && overview.length === 0) {
    return (
      <div style={containerStyle}>
        <h2 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '2rem' }}>
          All Tests Overview
        </h2>
        <div style={gridStyle}>
          {[...Array(6)].map((_, index) => (
            <div key={index} style={skeletonTileStyle}></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: '#fff5f5',
          border: '1px solid #fed7d7',
          borderRadius: '12px',
          color: '#e53e3e'
        }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Error Loading Results</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
  };

  const getSortedTiles = () => {
    let sortedTiles = [...tiles];

    // First sort by date if date sort is selected
    if (dateSort === 'recent') {
      sortedTiles.sort((a, b) => {
        if (!a.latestSubmission && !b.latestSubmission) return 0;
        if (!a.latestSubmission) return 1;
        if (!b.latestSubmission) return -1;
        return new Date(b.latestSubmission) - new Date(a.latestSubmission);
      });
    } else if (dateSort === 'oldest') {
      sortedTiles.sort((a, b) => {
        if (!a.latestSubmission && !b.latestSubmission) return 0;
        if (!a.latestSubmission) return -1;
        if (!b.latestSubmission) return 1;
        return new Date(a.latestSubmission) - new Date(b.latestSubmission);
      });
    }

    // Then apply secondary sort if configured
    if (sortConfig.key) {
      sortedTiles.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedTiles;
  };

  const sortedTiles = getSortedTiles();

  return (
    <div style={containerStyle}>
      <h2 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '0.5rem' }}>
        All Tests Overview
      </h2>
      {totalResults > 0 && (
        <p style={{ textAlign: 'center', color: '#6c757d', marginBottom: '2rem' }}>
          {totalResults} test results found
        </p>
      )}

      {/* Search Bar */}
      <form onSubmit={handleSearch} style={{
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '1rem',
  gap: '0.5rem',
  flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search tests by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            border: '2px solid #e9ecef',
            fontSize: '1rem',
            width: '300px',
            maxWidth: '100%',
            transition: 'border-color 0.2s ease'
          }}
        />
        <button
          type="submit"
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            fontSize: '1rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
          }}
        >
          Search
        </button>
      </form>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '0.25rem',
        marginBottom: '1rem',
        flexWrap: 'wrap'
      }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500',}}>
          <label htmlFor="dateSort" style={{
            fontSize: '0.875rem',
            color: '#6c757d',
            fontWeight: '500'
          }}>
            Order by:
          </label>
          <select
            id="dateSort"
            value={dateSort}
            onChange={handleDateSortChange}
            style={{
              padding: '0.5rem',
              borderRadius: '6px',
              border: '1px solid #e9ecef',
              fontSize: '0.875rem',
              cursor: 'pointer',
              backgroundColor: '#ffffff',
              minWidth: '120px'
            }}
          >
            <option value="recent">Most Recent First</option>
            <option value="oldest">Oldest First</option>
            <option value="none">Don't Sort by Date</option>
          </select>
        </div>
        <button
          onClick={() => handleSort('name')}
          style={{
              padding: '0.25rem 0.5rem',
              borderRadius: '6px',
              backgroundColor: sortConfig.key === 'name' ? '#3498db' : '#f8f9fa',
              color: sortConfig.key === 'name' ? 'white' : '#6c757d',
              border: `1px solid ${sortConfig.key === 'name' ? '#3498db' : '#e9ecef'}`,
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
          }}
        >
          Name{getSortIndicator('name')}
        </button>
        <button
          onClick={() => handleSort('attempts')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            backgroundColor: sortConfig.key === 'attempts' ? '#3498db' : '#f8f9fa',
            color: sortConfig.key === 'attempts' ? 'white' : '#6c757d',
            border: `1px solid ${sortConfig.key === 'attempts' ? '#3498db' : '#e9ecef'}`,
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Attempts{getSortIndicator('attempts')}
        </button>
        <button
          onClick={() => handleSort('avgAcc')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            backgroundColor: sortConfig.key === 'avgAcc' ? '#3498db' : '#f8f9fa',
            color: sortConfig.key === 'avgAcc' ? 'white' : '#6c757d',
            border: `1px solid ${sortConfig.key === 'avgAcc' ? '#3498db' : '#e9ecef'}`,
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Accuracy{getSortIndicator('avgAcc')}
        </button>
        <button
          onClick={() => handleSort('avgWpm')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            backgroundColor: sortConfig.key === 'avgWpm' ? '#3498db' : '#f8f9fa',
            color: sortConfig.key === 'avgWpm' ? 'white' : '#6c757d',
            border: `1px solid ${sortConfig.key === 'avgWpm' ? '#3498db' : '#e9ecef'}`,
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Speed{getSortIndicator('avgWpm')}
        </button>
      </div>

      {loading && (
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
          <p style={{ color: '#6c757d', marginTop: '1rem' }}>Loading test results...</p>
        </div>
      )}

      {tiles.length === 0 && !loading ? (
        <div style={{
  textAlign: 'center',
  padding: '2rem 1rem',
  background: '#f8f9fa',
  borderRadius: '12px',
  border: '1px solid #e9ecef'
        }}>
          <h3 style={{ color: '#6c757d', margin: '0 0 1rem 0' }}>No test results available</h3>
          <p style={{ color: '#6c757d' }}>Complete some tests to see your results here.</p>
        </div>
      ) : (
        <>
          <div style={gridStyle}>
            {sortedTiles.map((tile) => (
              <div
                key={tile.id}
                style={baseTileStyle}
                onClick={() => handleTileClick(tile.id)}
                className="tile-hover"
              >
                <div style={tileHeaderStyle}>
                  <h3 style={tileTitleStyle}>{tile.name}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={tileCategoryStyle}>{tile.category}</span>
                    {/* <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTest(tile.id, tile.name);
                      }}
                      style={userRole === 'admin' ? adminDeleteButtonStyle : deleteButtonStyle}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = userRole === 'admin' ? adminDeleteButtonHoverStyle.backgroundColor : deleteButtonHoverStyle.backgroundColor;
                        e.target.style.borderColor = userRole === 'admin' ? adminDeleteButtonHoverStyle.borderColor : deleteButtonHoverStyle.borderColor;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = userRole === 'admin' ? adminDeleteButtonStyle.backgroundColor : deleteButtonStyle.backgroundColor;
                        e.target.style.borderColor = userRole === 'admin' ? adminDeleteButtonStyle.borderColor : deleteButtonStyle.borderColor;
                      }}
                      disabled={loading}
                    >
                      {userRole === 'admin' ? 'Delete Test' : 'Delete My Results'}
                    </button> */}
                  </div>
                </div>

                <div style={tileStatsStyle}>
                  <div style={statItemStyle}>
                    <p style={statLabelStyle}>Accuracy</p>
                    <p style={statValueStyle}>{tile.avgAcc}%</p>
                  </div>
                  <div style={statItemStyle}>
                    <p style={statLabelStyle}>Speed</p>
                    <p style={statValueStyle}>{tile.avgWpm}</p>
                  </div>
                  <div style={statItemStyle}>
                    <span style={attemptsLabelStyle}>Attempts</span>
                    <span style={attemptsValueStyle}>{tile.attempts}</span>
                  </div>
                  {tile.latestSubmission && (
                    <div style={statItemStyle}>
                      <p style={statLabelStyle}>Last Attempt</p>
                      <p style={{ ...statValueStyle, fontSize: '0.875rem' }}>
                        {new Date(tile.latestSubmission).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Individual attempt deletion section - only show if user has submissions for this test */}
                {tile.submissions && tile.submissions.length > 0 && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: 'rgba(220, 53, 69, 0.05)',
                    borderRadius: '6px',
                    borderLeft: '3px solid #dc3545'
                  }}>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#6c757d',
                    margin: '0 0 0.5rem 0',
                    fontWeight: '600'
                    }}>
                      Recent Attempts:
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {tile.submissions.slice(0, 3).map((submission, index) => (
                        <div
                          key={submission.id || index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            background: 'rgba(255, 255, 255, 0.8)',
                            padding: '0.25rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}
                        >
                          <span style={{ color: '#495057' }}>
                            {new Date(submission.createdAt).toLocaleDateString()}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAttempt(tile.id, submission.id);
                            }}
                            style={{
                              padding: '0.125rem 0.25rem',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                            disabled={loading}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                      {tile.submissions.length > 3 && (
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#6c757d',
                          fontStyle: 'italic'
                        }}>
                          +{tile.submissions.length - 3} more attempts
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            ))}
          </div>

          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default ResultsOverview;
